import { Store } from '@reduxjs/toolkit';
import { logout, setNewTokens } from './reducers/Auth';

const PERSIST_AUTH_KEY = 'persist:auth';

interface PersistedAuth {
  isAuthenticated?: boolean;
  accessToken?: string | null;
  refreshToken?: string | null;
}

export const readPersistedAuth = (
  raw?: string | null
): PersistedAuth | null => {
  try {
    const source = raw ?? localStorage.getItem(PERSIST_AUTH_KEY);
    if (!source) return null;
    const outer = JSON.parse(source);
    if (!outer?.auth) return null;
    return JSON.parse(outer.auth);
  } catch {
    return null;
  }
};

export const writePersistedAuthTokens = (tokens: {
  accessToken: string;
  refreshToken: string;
}) => {
  try {
    const raw = localStorage.getItem(PERSIST_AUTH_KEY);
    if (!raw) return;
    const outer = JSON.parse(raw);
    if (!outer?.auth) return;
    const inner = JSON.parse(outer.auth);
    inner.accessToken = tokens.accessToken;
    inner.refreshToken = tokens.refreshToken;
    outer.auth = JSON.stringify(inner);
    localStorage.setItem(PERSIST_AUTH_KEY, JSON.stringify(outer));
  } catch {
    return;
  }
};

export const initAuthTokensSync = (store: Store) => {
  window.addEventListener('storage', (event) => {
    if (event.key !== PERSIST_AUTH_KEY) return;
    const persisted = readPersistedAuth(event.newValue);
    if (!persisted) return;

    const state = store.getState() as { auth?: { auth?: PersistedAuth } };
    const current = state.auth?.auth;

    if (!persisted.accessToken || !persisted.refreshToken) {
      if (current?.isAuthenticated) {
        store.dispatch(logout());
      }
      return;
    }

    /* Una pestaña sin sesión no adopta tokens ajenos: al cambiar su estado,
       redux-persist reescribe persist:auth con su isAuthenticated en false y la
       pestaña logueada cae al login en el próximo F5. */
    if (!current?.isAuthenticated) return;

    if (
      persisted.accessToken !== current?.accessToken ||
      persisted.refreshToken !== current?.refreshToken
    ) {
      store.dispatch(
        setNewTokens({
          accessToken: persisted.accessToken,
          refreshToken: persisted.refreshToken,
          userInfo: null,
        })
      );
    }
  });
};
